import { useState, useEffect, useRef, useCallback } from "react";
import { Search, ArrowRight, User, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface Suggestion {
  type: "skin" | "creator" | "tag";
  id: string;
  label: string;
  subtitle?: string;
  href: string;
}

const useDebounce = (value: string, ms: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
};

const HeroSection = () => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const debouncedQuery = useDebounce(query, 250);
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleSearch = () => {
    setShowDropdown(false);
    if (query.trim()) {
      navigate(`/explore?search=${encodeURIComponent(query.trim())}`);
    } else {
      navigate("/explore");
    }
  };

  // Fetch suggestions
  const fetchSuggestions = useCallback(async (term: string) => {
    if (term.length < 2) { setSuggestions([]); return; }

    const [{ data: skins }, { data: creators }] = await Promise.all([
      supabase
        .from("skins")
        .select("id, title, tags")
        .ilike("title", `%${term}%`)
        .order("likes_count", { ascending: false })
        .limit(4),
      supabase
        .from("profiles")
        .select("user_id, username")
        .ilike("username", `%${term}%`)
        .order("followers_count", { ascending: false })
        .limit(3),
    ]);

    const results: Suggestion[] = [];

    (skins || []).forEach((s) =>
      results.push({
        type: "skin",
        id: s.id,
        label: s.title,
        subtitle: (s.tags as string[] | null)?.slice(0, 2).join(", ") || undefined,
        href: `/skin/${s.id}`,
      })
    );

    (creators || []).forEach((c) =>
      results.push({
        type: "creator",
        id: c.user_id,
        label: c.username,
        subtitle: "Creator",
        href: `/creator/${c.user_id}`,
      })
    );

    // Add matching tags
    const lowerTerm = term.toLowerCase();
    const tagMatches = ["Boy", "Girl", "Fantasy", "Anime", "Gaming", "Animals", "Sci-Fi", "Medieval", "AI Generated"]
      .filter((t) => t.toLowerCase().includes(lowerTerm));
    tagMatches.slice(0, 2).forEach((t) =>
      results.push({
        type: "tag",
        id: `tag-${t}`,
        label: t,
        subtitle: "Category",
        href: `/explore?search=&category=${encodeURIComponent(t)}`,
      })
    );

    setSuggestions(results);
    setSelectedIdx(-1);
  }, []);

  useEffect(() => {
    fetchSuggestions(debouncedQuery);
  }, [debouncedQuery, fetchSuggestions]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      if (selectedIdx >= 0 && suggestions[selectedIdx]) {
        setShowDropdown(false);
        navigate(suggestions[selectedIdx].href);
      } else {
        handleSearch();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const iconFor = (type: Suggestion["type"]) => {
    if (type === "creator") return <User className="w-4 h-4 text-muted-foreground" />;
    if (type === "tag") return <Layers className="w-4 h-4 text-muted-foreground" />;
    return <Search className="w-4 h-4 text-muted-foreground" />;
  };

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-20">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-secondary/10" />
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-primary/30 animate-float"
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.8}s`,
              animationDuration: `${4 + i}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center">
        <div className="max-w-3xl mx-auto space-y-8">
          <h1 className="text-5xl md:text-7xl font-black leading-tight">
            <span>Browse &amp; Create</span>{" "}
            <span className="gradient-text">Amazing</span>{" "}
            <span>Minecraft Skins</span>
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto">
            Browse thousands of community-made skins, create your own, and share with players worldwide.
          </p>

          {/* Search bar with autocomplete */}
          <div ref={wrapperRef} className="relative max-w-lg mx-auto z-20">
            <div className="glass p-1.5 flex items-center gap-2 glow-purple">
              <Search className="w-5 h-5 text-muted-foreground ml-4" />
              <input
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
                onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                onKeyDown={handleKeyDown}
                placeholder="Search skins, creators, tags..."
                className="flex-1 bg-transparent border-0 outline-none text-foreground placeholder:text-muted-foreground py-3 px-2"
              />
              <Button onClick={handleSearch} className="bg-gradient-to-r from-primary to-secondary text-primary-foreground border-0 px-6 hover:opacity-90">
                Search
              </Button>
            </div>

            {/* Suggestions dropdown */}
            {showDropdown && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 mt-2 glass rounded-xl border border-border/50 overflow-hidden shadow-xl z-50">
                {suggestions.map((s, i) => (
                  <button
                    key={s.id}
                    onMouseDown={() => { navigate(s.href); setShowDropdown(false); }}
                    onMouseEnter={() => setSelectedIdx(i)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      i === selectedIdx ? "bg-muted/50" : "hover:bg-muted/30"
                    }`}
                  >
                    {iconFor(s.type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{s.label}</p>
                      {s.subtitle && <p className="text-xs text-muted-foreground">{s.subtitle}</p>}
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{s.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* CTAs */}
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/explore">
              <Button size="lg" className="bg-gradient-to-r from-primary to-secondary text-primary-foreground border-0 text-base px-8 hover:opacity-90 transition-opacity glow-purple">
                Explore Skins
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/editor">
              <Button size="lg" variant="outline" className="border-glass-border text-foreground hover:bg-muted/50 text-base px-8">
                Create Your Skin
              </Button>
            </Link>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center gap-8 md:gap-12 pt-8">
            {[
              { value: "50K+", label: "Skins" },
              { value: "12K+", label: "Creators" },
              { value: "1M+", label: "Downloads" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl font-bold gradient-text">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
